import { create } from 'zustand';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

// Promise-based imperative confirm — CONTRACTS.md §6. One method: confirm(config) => Promise<ConfirmResult>.
export interface ConfirmConfig {
  title: string;
  description?: string; // body text (NOT `message`)
  confirmText?: string; // default "Confirm"
  variant?: 'default' | 'destructive'; // (NOT `confirmVariant`)
  requireReason?: boolean; // when true, a reason textarea is shown & required
}
export interface ConfirmResult {
  confirmed: boolean;
  reason?: string;
}

interface ConfirmStore {
  config: ConfirmConfig | null;
  resolve: ((r: ConfirmResult) => void) | null;
  request: (config: ConfirmConfig) => Promise<ConfirmResult>; // internal; public API is useConfirm()
  settle: (r: ConfirmResult) => void;
}

const useConfirmStore = create<ConfirmStore>((set, get) => ({
  config: null,
  resolve: null,
  request: (config) => new Promise<ConfirmResult>((resolve) => set({ config, resolve })),
  settle: (r) => {
    get().resolve?.(r);
    set({ config: null, resolve: null });
  },
}));

/** The only public API — a stable callback returning a Promise<ConfirmResult>. */
export function useConfirm(): (config: ConfirmConfig) => Promise<ConfirmResult> {
  return useConfirmStore((s) => s.request);
}

/** Mount once at the app shell (parity: AppConfirmDialog + ConfirmDialogState). */
export function ConfirmDialogHost() {
  const config = useConfirmStore((s) => s.config);
  const settle = useConfirmStore((s) => s.settle);
  const [reason, setReason] = useState('');
  if (!config) return null;
  const blocked = config.requireReason === true && reason.trim() === '';

  const close = (confirmed: boolean) => {
    // Avoid an explicit `reason: undefined` (exactOptionalPropertyTypes) — omit it unless required.
    const result: ConfirmResult = config.requireReason ? { confirmed, reason } : { confirmed };
    settle(result);
    setReason('');
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) close(false);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
        </DialogHeader>
        {config.description && <p className="text-sm text-muted-foreground">{config.description}</p>}
        {config.requireReason && (
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Add a reason..."
            rows={4}
          />
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            variant={config.variant ?? 'destructive'}
            disabled={blocked}
            onClick={() => close(true)}
          >
            {config.confirmText ?? 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
